import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@app/context/ModalsContext'
import { useSnackbar } from '@app/context/SnackbarContext'

import { Body2 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { LessGreaterIcon } from '@ui/icons/LessGreater.icon'
import { QRCodeIcon } from '@ui/icons/QRCode.icon'

import { useGAEvents } from '@utils/callGAEvents'
import { copyToClipboard } from '@utils/navigator.utils'

import { Box } from '@shared/components/Box'

import { QrCodeModal } from './QrCodeModal.modal'

import styles from './ReferralLink.module.scss'

export const VARIANT = {
  DEFAULT: 'default',
  NO_LABEL: 'noLabel',
  ACTIONS_ONLY: 'actionsOnly',
} as const

export type TVariant = (typeof VARIANT)[keyof typeof VARIANT]

export const ACTION_BUTTON = {
  COPY: 'copy',
  SHORTEN: 'shorten',
  QR: 'qr',
} as const

export type TActionButton = (typeof ACTION_BUTTON)[keyof typeof ACTION_BUTTON]

interface Props {
  label?: string
  ibCode?: string
  referralLink?: string | null
  landingPageUrl?: string | null
  shortenedReferralLink?: string
  isShortLinkLoading: boolean
  onShortenIBLink: (referralCode: string) => Promise<void>
  isShorteningAvailable: boolean
  isCustomUrl: boolean
  variant?: TVariant
  shouldHideReferralLinkLabel?: boolean
  buttonVariant?: 'secondary' | 'ghost'
  placeholder?: string
  hiddenActions?: TActionButton[]
  isLinkHub?: boolean
}

export const ReferralLinkUrlField: FC<Props> = ({
  label,
  referralLink,
  landingPageUrl,
  isShortLinkLoading,
  shortenedReferralLink,
  ibCode,
  onShortenIBLink,
  isShorteningAvailable,
  isCustomUrl,
  variant = VARIANT.DEFAULT,
  shouldHideReferralLinkLabel,
  buttonVariant = 'secondary',
  placeholder,
  hiddenActions = [],
  isLinkHub,
}) => {
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()
  const { callGAEvents } = useGAEvents()

  const referralLinkValue = shortenedReferralLink || referralLink || ''
  const showReferralLink = variant !== VARIANT.ACTIONS_ONLY
  const showLabel = variant === VARIANT.DEFAULT && !shouldHideReferralLinkLabel
  const showMarginTop = variant !== VARIANT.ACTIONS_ONLY
  const ActionButton = buttonVariant === 'secondary' ? Button.Secondary : Button.Ghost

  const allActionButtons = Object.values(ACTION_BUTTON)
  const isActionHidden = (key: TActionButton) => hiddenActions.includes(key)
  const visibleActionButtons = allActionButtons.filter((key) => !isActionHidden(key))
  const hasVisibleActionButtons = visibleActionButtons.length > 0

  const qrCodeModal = useModal(QrCodeModal, {
    shortenedReferralLink,
    referralLink: referralLinkValue,
    landingPageUrl: landingPageUrl as string,
    isShortLinkLoading,
    isCustomUrl,
    ibCode,
    domain: window.location.host,
    generateShortenUrl: false,
  })
  const renderPlaceholder = () => {
    if (!showLabel) {
      return
    }

    return placeholder || t('IB.Dashboard.ReferralLink.Referral link')
  }

  const shouldDisableControls = (controlVariant: TActionButton) => {
    switch (controlVariant) {
      case ACTION_BUTTON.COPY:
        return (
          (variant === VARIANT.ACTIONS_ONLY && !landingPageUrl) || !ibCode || !referralLinkValue
        )
      case ACTION_BUTTON.SHORTEN:
        return (
          (variant === VARIANT.ACTIONS_ONLY && !landingPageUrl) ||
          !referralLinkValue ||
          !ibCode ||
          isShortLinkLoading ||
          !!shortenedReferralLink ||
          !isShorteningAvailable
        )
      case ACTION_BUTTON.QR:
        return (
          (variant === VARIANT.ACTIONS_ONLY && !landingPageUrl) || !ibCode || !referralLinkValue
        )
      default:
        return false
    }
  }

  return (
    <Box className={styles.referralLinkContainer}>
      {showReferralLink && (
        <Box flex direction='column' gap='x8'>
          {showLabel && (
            <Body2 as='label' htmlFor={t('IB.Dashboard.Referral Link for registration')}>
              {label ? label : t('IB.Dashboard.Referral Link for registration')}
            </Body2>
          )}
          <Input
            value={referralLinkValue}
            label={renderPlaceholder()}
            showLabel={!referralLinkValue}
            disabled={!referralLinkValue || isShortLinkLoading}
            size='lg'
            id={t('IB.Dashboard.Referral Link for registration')}
          />
        </Box>
      )}
      {hasVisibleActionButtons && (
        <Box flex mt={showMarginTop ? 'x8' : undefined} gap='x8'>
          {!isActionHidden(ACTION_BUTTON.COPY) && (
            <ActionButton
              size={isLinkHub ? 'md' : 'sm'}
              fullWidth
              iconLeft={CopyIcon}
              disabled={shouldDisableControls(ACTION_BUTTON.COPY)}
              onClick={() => {
                copyToClipboard(referralLinkValue)
                addSnackbar.success({ message: t('Copied') })
                callGAEvents().IBDashboard.ReferralLink.copyLink()
              }}
              loading={isShortLinkLoading}
            >
              {t('Copy')}
            </ActionButton>
          )}
          {!isActionHidden(ACTION_BUTTON.SHORTEN) && (
            <ActionButton
              size={isLinkHub ? 'md' : 'sm'}
              fullWidth
              iconLeft={LessGreaterIcon}
              disabled={shouldDisableControls(ACTION_BUTTON.SHORTEN)}
              loading={isShortLinkLoading}
              onClick={() => {
                if (!ibCode) return
                onShortenIBLink(ibCode)
              }}
            >
              {t('Shorten')}
            </ActionButton>
          )}
          {!isActionHidden(ACTION_BUTTON.QR) && (
            <ActionButton
              size={isLinkHub ? 'md' : 'sm'}
              fullWidth
              iconLeft={QRCodeIcon}
              disabled={shouldDisableControls(ACTION_BUTTON.QR)}
              loading={isShortLinkLoading}
              onClick={() => qrCodeModal.open()}
            >
              {t('QR')}
            </ActionButton>
          )}
        </Box>
      )}
    </Box>
  )
}
